/**
 * useVisionSLVisionCtaLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait VisionSLVisionCta
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, VisionSLVisionView as VisionSLVisionViewEntity, VisionSLVisionCtaEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type VisionSLVisionCtaState =
  | 'idle';

export type VisionSLVisionCtaEvent =
  | 'INIT'
  | 'HERO_PRIMARY'
  | 'HERO_SECONDARY'
  | 'CTA_PRIMARY'
  | 'CTA_SECONDARY';

type VisionSLVisionCtaSlot = 'main';
interface VisionSLVisionCtaInternalState {
  machineState: VisionSLVisionCtaState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: VisionSLVisionCtaEvent | null;
  slotWriter: Partial<Record<VisionSLVisionCtaSlot, VisionSLVisionCtaEvent | null>>;
  loading: boolean;
  pendingEvent: VisionSLVisionCtaEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<VisionSLVisionViewEntity>;
}

type VisionSLVisionCtaAction =
  | { type: 'EVENT_SUCCESS'; event: VisionSLVisionCtaEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: VisionSLVisionCtaSlot[]; renderClear?: VisionSLVisionCtaSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: VisionSLVisionCtaEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof VisionSLVisionViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function visionSLVisionCtaReducer(
  state: VisionSLVisionCtaInternalState,
  action: VisionSLVisionCtaAction
): VisionSLVisionCtaInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as VisionSLVisionCtaState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<VisionSLVisionViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<VisionSLVisionViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<VisionSLVisionViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: VisionSLVisionCtaInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface VisionSLVisionCtaConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  alignment: 'center' | 'start';
  backdropContent: { className: string; type: string };
  backgroundPosition: 'center' | 'top' | 'bottom' | 'left' | 'right';
  backgroundSrc: string;
  command: string;
  copyContent: { align: Array<string | string[]>; children: Array<{ align: Array<string | string[]>; className: Array<string | string[]>; color: Array<string | string[]>; content: string; type: string; variant: string } | { align: Array<string | string[]>; className: string; content: string; type: string; variant: Array<string | string[]>; weight: string } | { children: { action: string; actionPayload: { href: string }; className: Array<string | string[]>; href: string; label: string; size: string; type: string; variant: string }[]; className: Array<Array<string | string[]> | string | string[]>; direction: string; gap: string; justify: Array<string | string[]>; type: string; wrap: boolean } | { children: { code: string; language: string; showCopyButton: boolean; showLanguageBadge: boolean; type: string }[]; className: Array<string | string[]>; fullWidth: boolean; type: string } | { label: string; size: string; type: string; variant: string }>; direction: string; gap: string; type: string };
  edgeBottom: 'none' | 'curve' | 'wave' | 'arc' | 'tilt' | 'step' | 'scallop';
  edgeColor: 'background' | 'muted' | 'surface' | 'gradient' | 'inverse';
  edgeTop: 'none' | 'curve' | 'wave' | 'arc' | 'tilt' | 'step' | 'scallop';
  eyebrow: string;
  headline?: string;
  height: 'auto' | 'tall';
  illustration: string;
  illustrationContent: { alt: string; animate: boolean; animation: string; height: Array<number | string | string[]>; svgContent: string; type: string; width: string };
  lede?: string;
  look: 'hero' | 'banner' | 'header';
  mediaAlt: string;
  mediaContent: { alt: string; bordered: boolean; loading: string; rounded: string; src: string; type: string };
  mediaPosition: 'below' | 'side';
  mediaSrc: string;
  primaryHref: string;
  primaryLabel?: string;
  secondaryHref: string;
  secondaryLabel?: string;
  sideContent: { bg: Array<Array<Array<Array<string | string[]> | string> | string | string[]> | string | string[]>; children: Array<{ "aria-hidden": boolean; children: Array<string | { alt: string; className: string; fit: string; position: string; priority: string[]; src: string; type: string } | { className: Array<string | string[]>; type: string }>; className: string; position: string; type: string } | { children: { children: { bg: Array<Array<string | string[]> | string>; border: Array<string | string[]>; children: { align: string; children: Array<Array<string | string[]> | string>; gap: string; ratio: string; type: string }[]; padding: Array<string | string[]>; rounded: Array<string | string[]>; type: string }[]; maxWidth: string; padding: string; size: string; type: string }[]; fullWidth: boolean; position: string; type: string }>; className: Array<string | string[]>; edgeBottom: string; edgeColor: string; edgeTop: string; fullWidth: boolean; overflow: string; paddingY: Array<Array<string | string[]> | string | string[]>; position: string; rounded: Array<string | string[]>; texture: string; type: string };
  stackedContent: { bg: Array<Array<Array<Array<string | string[]> | string> | string | string[]> | string | string[]>; children: Array<{ "aria-hidden": boolean; children: Array<string | { alt: string; className: string; fit: string; position: string; priority: string[]; src: string; type: string } | { className: Array<string | string[]>; type: string }>; className: string; position: string; type: string } | { children: { children: { bg: Array<Array<string | string[]> | string>; border: Array<string | string[]>; children: { align: Array<string | string[]>; children: Array<Array<string | string[]> | string>; direction: string; gap: string; type: string }[]; padding: Array<string | string[]>; rounded: Array<string | string[]>; type: string }[]; maxWidth: string; padding: string; size: string; type: string }[]; fullWidth: boolean; position: string; type: string }>; className: Array<string | string[]>; edgeBottom: string; edgeColor: string; edgeTop: string; fullWidth: boolean; overflow: string; paddingY: Array<Array<string | string[]> | string | string[]>; position: string; rounded: Array<string | string[]>; texture: string; type: string };
  surface: 'plain' | 'muted' | 'accent' | 'inverse' | 'image';
  texture: boolean;
}

const DEFAULT_VISION_SL_VISION_CTA_CONFIG = {
  alignment: "center",
  backdropContent: { className: "hidden", type: "box" },
  backgroundPosition: "center",
  backgroundSrc: "",
  command: "",
  copyContent: { align: ["if", ["==", "@config.alignment", "center"], "center", "start"], children: [{ label: "@config.eyebrow", size: "md", type: "badge", variant: "primary" }, { align: ["if", ["==", "@config.alignment", "center"], "center", "left"], className: "tracking-tight text-balance", content: "@config.headline", type: "typography", variant: ["if", ["==", "@config.look", "hero"], "h1", "h2"], weight: "bold" }, { align: ["if", ["==", "@config.alignment", "center"], "center", "left"], className: ["if", ["==", "@config.lede", ""], "hidden", "max-w-2xl text-pretty"], color: ["if", ["==", "@config.look", "header"], "primary", "muted"], content: "@config.lede", type: "typography", variant: "large" }, { children: [{ action: "HERO_PRIMARY", actionPayload: { href: "@config.primaryHref" }, className: ["if", ["==", "@config.primaryLabel", ""], "hidden", ""], href: "@config.primaryHref", label: "@config.primaryLabel", size: "lg", type: "button", variant: "primary" }, { action: "HERO_SECONDARY", actionPayload: { href: "@config.secondaryHref" }, className: ["if", ["==", "@config.secondaryLabel", ""], "hidden", ""], href: "@config.secondaryHref", label: "@config.secondaryLabel", size: "lg", type: "button", variant: "secondary" }], className: ["if", ["==", "@config.primaryLabel", ""], ["if", ["==", "@config.secondaryLabel", ""], "hidden", "pt-2"], "pt-2"], direction: "horizontal", gap: "md", justify: ["if", ["==", "@config.alignment", "center"], "center", "start"], type: "stack", wrap: true }, { children: [{ code: "@config.command", language: "bash", showCopyButton: true, showLanguageBadge: false, type: "code-block" }], className: ["if", ["==", "@config.command", ""], "hidden", "max-w-xl"], fullWidth: true, type: "box" }], direction: "vertical", gap: "lg", type: "stack" },
  edgeBottom: "none",
  edgeColor: "background",
  edgeTop: "curve",
  eyebrow: "",
  height: "auto",
  illustration: "",
  illustrationContent: { alt: "", animate: true, animation: "draw", height: ["if", ["==", "@config.look", "header"], 200, 380], svgContent: "@config.illustration", type: "animated-graphic", width: "100%" },
  look: "banner",
  mediaAlt: "",
  mediaContent: { alt: "@config.mediaAlt", bordered: true, loading: "eager", rounded: "xl", src: "@config.mediaSrc", type: "image" },
  mediaPosition: "below",
  mediaSrc: "",
  primaryHref: "https://studio.almadar.io",
  secondaryHref: "mailto:hello@almadar.io",
  sideContent: { bg: ["if", ["==", "@config.surface", "muted"], "muted", ["if", ["==", "@config.surface", "accent"], "gradient", ["if", ["or", ["==", "@config.surface", "inverse"], ["==", "@config.surface", "image"]], "inverse", "transparent"]]], children: [{ "aria-hidden": true, children: [{ alt: "", className: "h-full", fit: "cover", position: "@config.backgroundPosition", priority: ["==", "@config.look", "hero"], src: "@config.backgroundSrc", type: "image" }, { className: ["if", ["==", "@config.surface", "image"], "band-scrim", "hidden"], type: "box" }, "@config.backdropContent"], className: "inset-0 pointer-events-none", position: "absolute", type: "box" }, { children: [{ children: [{ bg: ["if", ["and", ["==", "@config.look", "banner"], ["==", "@config.surface", "plain"]], "muted", "transparent"], border: ["and", ["==", "@config.look", "banner"], ["==", "@config.surface", "plain"]], children: [{ align: "center", children: ["@config.copyContent", ["if", ["==", "@config.illustration", ""], "@config.mediaContent", "@config.illustrationContent"]], gap: "xl", ratio: "1:1", type: "split" }], padding: ["if", ["==", "@config.look", "banner"], "xl", "none"], rounded: ["if", ["==", "@config.look", "banner"], "2xl", "none"], type: "box" }], maxWidth: "xl", padding: "lg", size: "xl", type: "container" }], fullWidth: true, position: "relative", type: "box" }], className: ["if", ["==", "@config.height", "tall"], "flex min-h-[80vh] items-center", ""], edgeBottom: "@config.edgeBottom", edgeColor: "@config.edgeColor", edgeTop: "@config.edgeTop", fullWidth: true, overflow: "hidden", paddingY: ["if", ["==", "@config.look", "hero"], "2xl", ["if", ["==", "@config.look", "banner"], "xl", "lg"]], position: "relative", rounded: ["if", ["==", "@config.look", "header"], "2xl", "none"], texture: "@config.texture", type: "box" },
  stackedContent: { bg: ["if", ["==", "@config.surface", "muted"], "muted", ["if", ["==", "@config.surface", "accent"], "gradient", ["if", ["or", ["==", "@config.surface", "inverse"], ["==", "@config.surface", "image"]], "inverse", "transparent"]]], children: [{ "aria-hidden": true, children: [{ alt: "", className: "h-full", fit: "cover", position: "@config.backgroundPosition", priority: ["==", "@config.look", "hero"], src: "@config.backgroundSrc", type: "image" }, { className: ["if", ["==", "@config.surface", "image"], "band-scrim", "hidden"], type: "box" }, "@config.backdropContent"], className: "inset-0 pointer-events-none", position: "absolute", type: "box" }, { children: [{ children: [{ bg: ["if", ["and", ["==", "@config.look", "banner"], ["==", "@config.surface", "plain"]], "muted", "transparent"], border: ["and", ["==", "@config.look", "banner"], ["==", "@config.surface", "plain"]], children: [{ align: ["if", ["==", "@config.alignment", "center"], "center", "stretch"], children: ["@config.copyContent", ["if", ["==", "@config.illustration", ""], "@config.mediaContent", "@config.illustrationContent"]], direction: "vertical", gap: "2xl", type: "stack" }], padding: ["if", ["==", "@config.look", "banner"], "xl", "none"], rounded: ["if", ["==", "@config.look", "banner"], "2xl", "none"], type: "box" }], maxWidth: "xl", padding: "lg", size: "xl", type: "container" }], fullWidth: true, position: "relative", type: "box" }], className: ["if", ["==", "@config.height", "tall"], "flex min-h-[80vh] items-center", ""], edgeBottom: "@config.edgeBottom", edgeColor: "@config.edgeColor", edgeTop: "@config.edgeTop", fullWidth: true, overflow: "hidden", paddingY: ["if", ["==", "@config.look", "hero"], "2xl", ["if", ["==", "@config.look", "banner"], "xl", "lg"]], position: "relative", rounded: ["if", ["==", "@config.look", "header"], "2xl", "none"], texture: "@config.texture", type: "box" },
  surface: "accent",
  texture: true,
} as VisionSLVisionCtaConfig;

export interface VisionSLVisionCtaLogicReturn {
  state: VisionSLVisionCtaState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: VisionSLVisionCtaEvent | null;
  slotWriter: Partial<Record<VisionSLVisionCtaSlot, VisionSLVisionCtaEvent | null>>;
  loading: boolean;
  pendingEvent: VisionSLVisionCtaEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<VisionSLVisionViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof VisionSLVisionCtaEventPayloadMap]: (payload?: VisionSLVisionCtaEventPayloadMap[K]) => Promise<EventResponse> };
  config: VisionSLVisionCtaConfig;
}

export function useVisionSLVisionCtaLogic(config?: Partial<VisionSLVisionCtaConfig>): VisionSLVisionCtaLogicReturn {
  const mergedConfig: VisionSLVisionCtaConfig = { ...DEFAULT_VISION_SL_VISION_CTA_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/vision-sl-vision-cta', orbitalName: 'VisionSL', traitName: 'VisionSLVisionCta' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(visionSLVisionCtaReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['VisionSLVisionView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        if (false) {

        } else {

        }

      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'HERO_PRIMARY') {
      // idle → idle (on HERO_PRIMARY)
        _emits.push(((_p) => () => { eventBus.emit('CTA_PRIMARY', _p, { trait: 'VisionSLVisionCta' }); emitCascadeEvent(eventBus, 'VisionSL', 'VisionSLVisionCta', { event: 'CTA_PRIMARY', payload: _p, source: { orbital: 'VisionSL', trait: 'VisionSLVisionCta' } }); })({ href: payloadObj?.href }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'HERO_SECONDARY') {
      // idle → idle (on HERO_SECONDARY)
        _emits.push(((_p) => () => { eventBus.emit('CTA_SECONDARY', _p, { trait: 'VisionSLVisionCta' }); emitCascadeEvent(eventBus, 'VisionSL', 'VisionSLVisionCta', { event: 'CTA_SECONDARY', payload: _p, source: { orbital: 'VisionSL', trait: 'VisionSLVisionCta' } }); })({ href: payloadObj?.href }));
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: VisionSLVisionCtaSlot[]; clear: VisionSLVisionCtaSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as VisionSLVisionCtaSlot[], clear: [] as VisionSLVisionCtaSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'VisionSLVisionView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as VisionSLVisionCtaEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'VisionSLVisionCta': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof VisionSLVisionCtaEventPayloadMap]: (payload?: VisionSLVisionCtaEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'HERO_PRIMARY': (payload?: VisionSLVisionCtaEventPayloadMap['HERO_PRIMARY']) => dispatch('HERO_PRIMARY', payload),
    'HERO_SECONDARY': (payload?: VisionSLVisionCtaEventPayloadMap['HERO_SECONDARY']) => dispatch('HERO_SECONDARY', payload),
    'CTA_PRIMARY': (payload?: VisionSLVisionCtaEventPayloadMap['CTA_PRIMARY']) => dispatch('CTA_PRIMARY', payload),
    'CTA_SECONDARY': (payload?: VisionSLVisionCtaEventPayloadMap['CTA_SECONDARY']) => dispatch('CTA_SECONDARY', payload),
  };

  useUIEvents(enqueueEvent, 'VisionSL.VisionSLVisionCta', ['INIT', 'HERO_PRIMARY', 'HERO_SECONDARY'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('VisionSLVisionCta', (): TraitStateSnapshot => ({
      traitName: 'VisionSLVisionCta',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'HERO_PRIMARY', 'HERO_SECONDARY', 'CTA_PRIMARY', 'CTA_SECONDARY'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  mergedConfig.headline = mergedConfig.headline ?? (__t("std-almadar-vision:cta.headline"));
  mergedConfig.lede = mergedConfig.lede ?? (__t("std-almadar-vision:cta.lede"));
  mergedConfig.primaryLabel = mergedConfig.primaryLabel ?? (__t("std-almadar-vision:cta.primary"));
  mergedConfig.secondaryLabel = mergedConfig.secondaryLabel ?? (__t("std-almadar-vision:cta.secondary"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
